import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";

import {
  ACCOUNT_MISMATCH_EVENT,
  ApiError,
  api,
  AUTH_REQUIRED_EVENT,
  haltRequests,
  SESSION_LOCKED_EVENT,
} from "../api/client";
import type { AuthEvent, AuthSession } from "../api/types";
import { licenceScope, licenceStore } from "../state/licenceScope";
import { useUi } from "../state/ui";
import { unsavedDrafts } from "../state/unsavedDrafts";
import { drainOrderEditors, haltOrderQueues } from "../views/order/orderSaveQueue";
import type { MountedAccount, OverlayName } from "./authState";

export { ACCOUNT_MISMATCH_EVENT, AUTH_REQUIRED_EVENT, SESSION_LOCKED_EVENT };
export { mountedScope } from "./mounted";

/** Comptes nominatifs côté client (docs/17 §8.1) : session de ce navigateur, compte monté, surcouche, fenêtres du
 *  menu du compte et gestes qui quittent la session (changer d'utilisateur, se déconnecter). */

/** Clé de la requête de session (GET /api/auth/session). */
export const AUTH_SESSION_QUERY = ["auth-session"] as const;

/** Session de ce navigateur ; null quand le serveur n'a pas les routes des comptes (indicateur `off` : 404). */
export async function readSession(): Promise<AuthSession | null> {
  try {
    return await api.auth.session();
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** Session de ce navigateur, telle que le portail l'a lue (sans relecture propre). */
export function useSession(enabled = true) {
  return useQuery({ queryKey: AUTH_SESSION_QUERY, queryFn: readSession, enabled, staleTime: 60_000, retry: false });
}

export type AccountDialog = "password" | "sessions" | "events" | "since" | "accounts" | null;

interface AuthStore {
  /** Compte pour lequel l'application est montée (null : mode hérité, ou pas encore monté). */
  mounted: MountedAccount | null;
  /** Fonction et rôle du compte monté (en-tête du menu du compte). */
  appointment: string | null;
  admin: boolean;
  /** Surcouche posée par un refus ou par l'inactivité, avant la relecture de la session. */
  hint: OverlayName | null;
  hintAt: number;
  /** Surcouche posée par un refus du serveur (401) : levée si une session relue ensuite est ouverte pour la même
   *  personne (refus d'une requête partie avant la reprise). Posée par ce navigateur (inactivité, menu, autre
   *  onglet) : seul le mot de passe la lève, même si le serveur n'a pas pu verrouiller. */
  hintFromServer: boolean;
  /** Heure du verrouillage vu par ce document (texte de la surcouche, si le serveur ne la rend pas). */
  lockedAt: number | null;
  dialog: AccountDialog;
  /** « Depuis votre dernière connexion », rendu par la dernière connexion. */
  since: AuthEvent[];
  /** Gestes installés par le portail. */
  actions: { lock: () => void; switchUser: () => void; signOut: () => void };
  setHint: (hint: OverlayName | null, fromServer?: boolean) => void;
  openDialog: (dialog: AccountDialog) => void;
}

const noop = () => undefined;

export const useAuth = create<AuthStore>((set) => ({
  mounted: null,
  appointment: null,
  admin: false,
  hint: null,
  hintAt: 0,
  hintFromServer: false,
  lockedAt: null,
  dialog: null,
  since: [],
  actions: { lock: noop, switchUser: noop, signOut: noop },
  setHint: (hint, fromServer = false) =>
    set((s) => ({
      hint,
      hintAt: Date.now(),
      // Une surcouche posée par ce navigateur le reste, même si un refus du serveur la confirme ensuite.
      hintFromServer: hint !== null && fromServer && (s.hint === null || s.hintFromServer),
      lockedAt: hint === "locked" ? (s.lockedAt ?? Date.now()) : hint ? s.lockedAt : null,
    })),
  openDialog: (dialog) => set({ dialog }),
}));

/** Compte monté (null en mode hérité) : le menu du compte ne s'affiche que pour lui. */
export function useMountedAccount(): MountedAccount | null {
  return useAuth((s) => s.mounted);
}

/** État de l'interface relu sous la portée de la personne (« switched », « migrated ») : l'état en mémoire était celui
 *  de la portée précédente (licence, autre personne), jamais montré, et ne doit pas se ranger sous la nouvelle. */
export function reloadUiState(): void {
  const stored = licenceStore.get("generex-ui");
  useUi.setState(useUi.getInitialState(), true);
  if (stored !== null) {
    licenceStore.set("generex-ui", stored);
    void useUi.persist.rehydrate();
  }
}

// --- Quitter la session --------------------------------------------------------------------------------------------

/** Étapes d'un départ de la session, injectables pour les essais. */
export interface LeaveDeps {
  drain: () => void;
  snapshot: () => void;
  haltQueues: () => void;
  haltRequests: () => void;
  logout: () => Promise<unknown>;
  clearCache: () => void;
  broadcast: () => void;
  leaveScope: () => void;
  freeze: () => void;
  reload: () => void;
}

/** « Changer d'utilisateur » (docs/17 §4.8), depuis une surcouche ou le menu du compte, et réaction à une session
 *  ouverte pour un autre compte (`logout` faux : la session présente est celle d'un autre) :
 *  1. saisies versées dans les copies locales de la personne, sous sa portée ;
 *  2. files arrêtées sans rien envoyer, plus aucune requête de l'application montée ;
 *  3. déconnexion si la session existe encore (permise sur une session verrouillée) ;
 *  4. cache vidé, changement diffusé aux autres onglets, page rechargée entièrement. */
export async function leaveSession(deps: LeaveDeps, { logout = true }: { logout?: boolean } = {}): Promise<void> {
  deps.drain();
  deps.snapshot();
  deps.haltQueues();
  deps.haltRequests();
  if (logout) {
    try {
      await deps.logout();
    } catch {
      /* session déjà fermée, ou serveur injoignable : le rechargement montre l'écran qui convient */
    }
  }
  deps.clearCache();
  deps.broadcast();
  deps.leaveScope();
  deps.freeze();
  deps.reload();
}

const LOGOUT_URL = "/api/auth/logout";

/** Étapes réelles d'un départ (application). `erase` : déconnexion volontaire, les brouillons de la personne
 *  quittent ce navigateur ; sinon ils restent sous sa portée. */
export function appLeaveDeps(options: {
  clearCache: () => void;
  broadcast: () => void;
  erase?: boolean;
  reloadAfterMs?: number;
}): LeaveDeps {
  return {
    drain: drainOrderEditors,
    snapshot: () => unsavedDrafts.snapshotAll(),
    haltQueues: haltOrderQueues,
    haltRequests: () => haltRequests((url) => url === LOGOUT_URL),
    logout: () => api.auth.logout(),
    clearCache: options.clearCache,
    broadcast: options.broadcast,
    leaveScope: () => (options.erase ? licenceScope.signOut() : licenceScope.leaveAccount()),
    freeze: () => {
      licenceScope.freeze();
      unsavedDrafts.close();
    },
    reload: () => {
      if (options.reloadAfterMs) window.setTimeout(() => window.location.reload(), options.reloadAfterMs);
      else window.location.reload();
    },
  };
}

/** Avis montré sur l'écran de connexion après une déconnexion volontaire (le rechargement efface les avis). */
export const SIGNED_OUT_KEY = "generex:deconnecte";
/** « Plus tard » de la proposition de bascule : jusqu'à la fin de la session du navigateur (§8.3), pour tous ses
 *  onglets et fenêtres. Cookie de session (sans échéance : le navigateur l'efface à sa fermeture), car le stockage de
 *  session est propre à chaque onglet. Aucune donnée : la seule marque « 1 », que le serveur ignore. */
export const LATER_COOKIE = "generex_comptes_plus_tard";

export function laterChosen(): boolean {
  try {
    return document.cookie.split(";").some((part) => part.trim() === `${LATER_COOKIE}=1`);
  } catch {
    return false;
  }
}

export function chooseLater(): void {
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${LATER_COOKIE}=1; Path=/; SameSite=Strict${secure}`;
  } catch {
    /* cookies refusés : la proposition revient au prochain chargement */
  }
}

export function sessionFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function setSessionFlag(key: string, on: boolean): void {
  try {
    if (on) window.sessionStorage.setItem(key, "1");
    else window.sessionStorage.removeItem(key);
  } catch {
    /* stockage refusé : sans effet durable */
  }
}

/** Saisies non enregistrées de ce document, pour l'avertissement de déconnexion. */
export function unsavedLabels(): string[] {
  return unsavedDrafts.list().map((entry) => entry.label);
}
